import type { Submittal } from '@/lib/apiClient';
import { toDateInputValue, isRealYmd, isDateInRange, DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';

// Option lists for the Type and Reviewer pickers (each with an "Other…" escape
// hatch via SelectWithOther). Shared by the create form and the edit form so the
// two can't offer different choices.
export const SUBMITTAL_TYPE_OPTIONS = ['Shop Drawings', 'Product Data', 'Samples', 'Mock-up', 'Mix Design', 'Calculations'];
export const SUBMITTAL_REVIEWER_OPTIONS = ['Architect', 'Structural Eng.', 'MEP Eng.', 'AHJ', 'Owner'];

/**
 * Length limits for the plain text inputs. These mirror create-submittal.dto.ts,
 * which is the authority — the API still refuses anything longer. They are here so
 * the box stops accepting characters instead of failing on Save with a validator
 * message. Reviewer and Type go through SelectWithOther's "Other…" box, which has
 * no length prop, so only the API limits those.
 */
export const SUBMITTAL_FIELD_MAX = { title: 300, submittalNumber: 40, specSection: 40 } as const;

/**
 * The fields the edit form changes: the ones PATCH /submittals/:id accepts that
 * are NOT owned by the review workflow. Status, decision, ball in court, review
 * due and the review window each have their own control and their own event, so
 * an edit never writes them.
 */
export interface SubmittalEditForm {
  submittalNumber: string;
  title: string;
  specSection: string;
  submittalType: string;
  reviewer: string;
  receivedAt: string; // YYYY-MM-DD, for <input type="date">
}

/** The form as it stands before anyone touches it. A null column is an empty box. */
export function formFromSubmittal(sub: Submittal): SubmittalEditForm {
  return {
    submittalNumber: sub.submittalNumber ?? '',
    title: sub.title ?? '',
    specSection: sub.specSection ?? '',
    submittalType: sub.submittalType ?? '',
    reviewer: sub.reviewer ?? '',
    // A stored calendar date — read it with the UTC slice, never local getters.
    receivedAt: toDateInputValue(sub.receivedAt),
  };
}

/**
 * Only the fields that differ from what is saved, trimmed — the PATCH body.
 *
 * Unchanged fields are left out rather than re-sent, so an edit that touches the
 * reviewer can't overwrite a spec section someone else changed in the meantime,
 * and "nothing changed" is an empty object (Save stays disabled).
 *
 * A field the PM emptied is sent as '' — the API turns that into NULL for the
 * optional ones. null/undefined can't carry it: on the wire both mean "leave it".
 */
export function buildEditPatch(sub: Submittal, form: SubmittalEditForm): Partial<SubmittalEditForm> {
  const saved = formFromSubmittal(sub);
  const patch: Partial<SubmittalEditForm> = {};
  // The keys come from the form itself, not a second hand-kept list: a field added
  // to SubmittalEditForm is compared (and sent) without anyone remembering to add it
  // here, instead of being edited, "saved", and silently dropped.
  for (const k of Object.keys(saved) as Array<keyof SubmittalEditForm>) {
    const next = form[k].trim();
    if (next !== saved[k].trim()) patch[k] = next;
  }
  return patch;
}

/**
 * Why this form can't be saved yet, or null.
 *
 * A submittal needs a title (the column is NOT NULL). A Received date can be
 * CHANGED but not removed: the PATCH has no way to clear a date, so emptying one
 * would be dropped and the old date silently kept. That only applies to a date
 * that exists — a submittal logged before the column did has none, and must stay
 * editable without the PM inventing one.
 *
 * A date the PM CHANGES must be a real day inside DATE_INPUT_MIN..MAX. The date
 * input's own `min`/`max` only paints it invalid: Save is a button, not a form
 * submit, so nothing in the browser stops "0026-10-08" (a two-digit year a browser
 * zero-pads) from going out. The API refuses it too; this is so the PM is told
 * here, not by an error banner after the click. A date that is already saved and
 * untouched is not checked: the edit never sends it, and a record that already
 * holds a bad date must still be editable, or fixing a title would first mean
 * fixing a date nobody asked about.
 */
export function editProblem(sub: Submittal, form: SubmittalEditForm): string | null {
  if (form.title.trim() === '') return 'A submittal needs a title.';
  const received = form.receivedAt.trim();
  const saved = formFromSubmittal(sub).receivedAt;
  if (received === '' && saved !== '') return 'Enter the date the package was received.';
  if (received !== '' && received !== saved) return receivedRangeProblem(received);
  return null;
}

/**
 * Why a received day is not one the API will take, or null: it has to be a real
 * calendar day inside DATE_INPUT_MIN..MAX (the API refuses anything else with a 400).
 * One copy, for every screen that sends a received day: the edit form and Send Revision.
 */
export function receivedRangeProblem(day: string): string | null {
  if (isRealYmd(day) && isDateInRange(day, DATE_INPUT_MIN, DATE_INPUT_MAX)) return null;
  return `Received must be a real date between ${DATE_INPUT_MIN.slice(0, 4)} and ${DATE_INPUT_MAX.slice(0, 4)}.`;
}

/**
 * Another submittal on the same job already carrying this number, if any. Only
 * ever a WARNING: the number is free text and nothing forbids a repeat, but it
 * feeds the Job Folder file names, so two with one number is worth a second look.
 * Case and spacing are ignored ("sub-4" collides with "SUB-4").
 */
export function submittalWithNumber(
  all: readonly Submittal[],
  selfId: number,
  number: string,
): Submittal | undefined {
  const norm = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, '').toLowerCase();
  const want = norm(number);
  if (want === '') return undefined;
  return all.find((s) => s.id !== selfId && norm(s.submittalNumber) === want);
}
